import { useNavigation } from 'hooks/useNavigation/useNavigation'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { OrderCard } from 'my-purchases/components/OrderCard/OrderCard'
import { RetryPartialError } from 'my-purchases/components/RetryPartialError/RetryPartialError'
import { PURCHASES_ERROR_TYPES } from 'my-purchases/constants/purchasesErrorTypes'
import type { ResponseError } from 'my-purchases/types/purchaseCommon'
import type { NormalizedPurchase } from 'my-purchases/types/purchaseList'
import { sendOnClickPurchaseDetail } from 'my-purchases/utils/analytics/analyticsEvents'

import styles from './PurchaseList.module.scss'
import text from 'fukku/styles/texts.module.scss'

interface PurchaseListProps {
	readonly title: string
	readonly isActive: boolean
	readonly retryFn?: () => void
	readonly purchases?: NormalizedPurchase[] | null
	readonly error?: ResponseError | null
}

export function PurchaseList({
	title,
	isActive,
	retryFn,
	purchases,
	error,
}: PurchaseListProps) {
	const { t } = useLabels()
	const { navigate } = useNavigation()
	const { errorType } = error ?? {}

	const dataTestId = `myPurchases.${isActive ? 'active' : 'inactive'}.purchases.list`

	return (
		<>
			<h2 className={`${text.titleL} ${styles.title}`}>{t(title)}</h2>
			{errorType && (
				<RetryPartialError
					active={isActive}
					fullError={errorType === PURCHASES_ERROR_TYPES.SERVER_ERROR}
					retryFn={retryFn}
				/>
			)}

			{purchases && purchases.length > 0 && (
				<ul className={styles.container} data-testid={dataTestId}>
					{purchases.map((purchase) => {
						const {
							link,
							type,
							orderIds,
							purchaseId,
							statusFormatted,
							articlesQuantity,
							firstLineColorId,
							creationDateTime,
							priceAmountFormatted,
							firstLineArticleImageId,
						} = purchase

						const handleClick = () => {
							sendOnClickPurchaseDetail(purchase, isActive)
							navigate(link)
						}

						return (
							<li key={purchaseId} className={styles.card}>
								<OrderCard
									orderIds={orderIds}
									noImageText={t(
										'myPurchases.details.imageNotAvailable.bodycopy'
									)}
									priceAmount={priceAmountFormatted}
									orderLineCount={articlesQuantity}
									status={statusFormatted}
									onClick={handleClick}
									buttonLabel={t('my_purchases.cta.view_details')}
									type={type}
									date={creationDateTime}
									articleImageId={firstLineArticleImageId}
									colorId={firstLineColorId}
								/>
							</li>
						)
					})}
				</ul>
			)}
		</>
	)
}
